/**
 * Function: addPositionToPortfolio
 *
 * Converts a selected position into a portfolio allocation if it does not
 * already exist. Normalizes asset type, resolves currency (position currency
 * or user default), and inserts an allocation with value locked and
 * allocation_percentage set to 0. Returns true if a new allocation was
 * created; false if it already existed or input was invalid.
 */
import { logger } from '@/services/LoggingService';
import { determineStandardAssetClass } from '@/utils/asset-class';
import { mapPositionToAssetType } from './mapPositionToAssetType';
import type { AnyPosition, PositionDisplay } from './types';
import { useAuthStore } from '@/stores/authStore';
import type { TablesInsert } from '@/integrations/supabase/types';
import { findAllocationId, insertAllocation } from '@/services/portfolio/PortfolioAllocationsService';
import { getDefaultCurrency } from '@/services/profile/UserProfileService';

export async function addPositionToPortfolio(position: AnyPosition, targetPortfolioId: string): Promise<boolean> {
  if (!position) {
    logger.error('AddPositionsToPortfolioDialog', 'Invalid position', { position });
    return false;
  }
  try {
    const user = useAuthStore.getState().user;
    if (!user?.id) {
      logger.error('AddPositionsToPortfolioDialog', 'User not authenticated');
      return false;
    }

    if (!position.id) {
      logger.error('AddPositionsToPortfolioDialog', 'Position missing required ID field', {
        positionId: position.id,
        userId: user.id,
        positionDetails: position,
      });
      return false;
    }

    const holdingId = position.id;
    const assetType = 'asset_type' in position && position.asset_type && typeof position.asset_type === 'string'
      ? position.asset_type
      : mapPositionToAssetType(position);

    const normalizedAssetType = determineStandardAssetClass(undefined, assetType);

    const pd = position as PositionDisplay;
    logger.debug('AddPositionsToPortfolioDialog', 'Processing position for portfolio allocation', {
      positionId: position.id,
      positionName: pd.name,
      symbol: pd.symbol,
      securityType: pd.securityType,
      marketValue: pd.market_value,
      sourceAssetType: 'asset_type' in position ? pd.asset_type : 'from_securityType',
      finalAssetType: normalizedAssetType,
      portfolioId: targetPortfolioId,
    });

    const existingId = await findAllocationId(targetPortfolioId, holdingId);
    if (!existingId) {
      const rawValue = typeof pd.market_value === 'number' ? pd.market_value : 0;
      if (rawValue < 0) {
        logger.info('AddPositionsToPortfolioDialog', 'Skipping position with negative market value', {
          holdingId,
          assetName: pd.name || 'Unknown',
          marketValue: rawValue,
        });
        return false;
      }
      if (rawValue === 0) {
        logger.debug('AddPositionsToPortfolioDialog', 'Including $0 position for predictable UX', {
          holdingId,
          assetName: pd.name || 'Unknown',
        });
      }
      const allocationValue = Math.round(rawValue * 100) / 100;
      // Derive currency code from position if available; fallback to user's default currency, else CAD
      let currencyCode: string | null = null;
      try {
        const pc = (position as any)?.currency;
        if (pc && typeof pc === 'string' && pc.trim()) {
          currencyCode = pc.trim().toUpperCase();
        } else {
          const userId = useAuthStore.getState().user?.id;
          if (userId) {
            const dc = await getDefaultCurrency(userId);
            currencyCode = dc || 'CAD';
          } else {
            currencyCode = 'CAD';
          }
        }
      } catch {
        currencyCode = 'CAD';
      }
      const payload: TablesInsert<'portfolio_allocations'> = {
        portfolio_id: targetPortfolioId,
        asset_id: holdingId,
        asset_type: normalizedAssetType,
        allocation_percentage: 0,
        allocation_value: allocationValue,
        is_value_locked: true,
        currency_code: currencyCode,
      };
      await insertAllocation(payload);
    } else {
      logger.info('AddPositionsToPortfolioDialog', 'Allocation already exists, skipping', {
        holdingId,
        portfolioId: targetPortfolioId,
      });
      return false;
    }
    return true;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    logger.error('AddPositionsToPortfolioDialog', 'Error adding position to portfolio', {
      error: message,
      position,
    });
    return false;
  }
}
